import { GetInvitationResponse, LicenseAssignment, Subscription, UserMembershipView } from '@localstack/types';

import { Filter, LicenseAssignmentsByUser, SeatAvailability } from './types';

/**
 * Compute the basic subscription stats for the given subscription index.
 * @param subscriptions - indexed subscription by ID.
 * @param assignments - indexed license assignments.
 */
export const computeSubscriptionFreeSeatAvailability = (
  subscriptions: Record<string, Subscription>,
  assignments: LicenseAssignmentsByUser,
): SeatAvailability => {
  const state: SeatAvailability = {};
  for (const [subId, sub] of Object.entries(subscriptions)) {
    state[subId] = { taken: 0, free: sub.seats, total: sub.seats };
  }
  for (const assignment of Object.values(assignments)) {
    for (const subId of assignment.subscriptions) {
      if (!state[subId]) continue;
      state[subId].taken += 1;
      state[subId].free -= 1;
    }
  }
  return state;
};

/**
 * Transformer to turn the list of memberships into an index of memberships.
 * @param members - the raw list of memberships.
 */
export const indexMembersById = (members: UserMembershipView[]): Record<string, UserMembershipView> =>
  Object.fromEntries([...members].sort((a, b) => b.created - a.created).map((member) => [member.user_id, member]));

/**
 * Transformer to turn the list of invitations to an index of invitations.
 * @param invitations - raw invitations.
 */
export const indexInvitationsById = (invitations: GetInvitationResponse[]): Record<string, GetInvitationResponse> =>
  Object.fromEntries(
    [...invitations].sort((a, b) => b.created - a.created).map((invitation) => [invitation.id, invitation]),
  );

/**
 * Group raw backend license assignments by user id. A user may have multiple
 * assignment records (one per assigned product), all of which are merged into a
 * single flattened view so the UI can show every license a user holds.
 */
export const indexAssignmentsByUserId = (assignments: LicenseAssignment[]): LicenseAssignmentsByUser =>
  assignments.reduce<LicenseAssignmentsByUser>((acc, { user_id, id, subscription_ids = [] }) => {
    if (!user_id || !id) return acc;

    const existing = acc[user_id]?.subscriptions ?? [];
    acc[user_id] = {
      subscriptions: Array.from(new Set([...existing, ...subscription_ids])),
    };

    return acc;
  }, {});

/**
 * Computes the total number of seats for the given index of subscriptions.
 * @param subscriptions - indexed subscription by ID
 */
export const computeTotalNumberOfSeats = (subscriptions: Record<string, Subscription>): number =>
  Object.values(subscriptions).reduce((memo, sub) => memo + sub.seats, 0);

const matchesSearchQuery = (searchQuery: string | null, ...fields: string[]): boolean => {
  const haystack = fields.join(' ').toLowerCase();
  const terms = (searchQuery ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((term) => haystack.includes(term));
};

/**
 * Applies the filters to the user memberships (excl. the invitations).
 *
 * - If the filter is set to "ALL" -> simply return the query results
 * - If the filter is set to "ACTIVE" -> simply return the query results (memberships are always active)
 * - If the filter is set to "INVITED" -> return nothing, as memberships != invitations
 * - If the filter is set to "ACTIVE + ASSIGNED" -> return members with assigned licenses
 *     - additionally check if the subscription filter is set, if so -> use assignments of that specific sub
 * - If the filter is set to "ACTIVE + UNASSIGNED" -> return members with no licenses
 *     - additionally check if the subscription filter is set, if so -> return memberships NOT ASSIGNED to sub
 *
 * @param filter - chosen membership filter
 * @param searchQuery - text search query to match
 * @param licenseAssignmentsState - captured dictionary with the current state of license assignments
 * @param subscriptionId - ID of a chosen subscription filter (if set)
 */
export const applyMembershipFilter =
  (
    filter: Filter,
    searchQuery: string | null,
    licenseAssignmentsState: LicenseAssignmentsByUser,
    subscriptionId: string,
  ) =>
  (membership: UserMembershipView): boolean => {
    const matchesSearch = matchesSearchQuery(searchQuery, membership.user_email, membership.user_full_name);

    if (filter === Filter.ALL || filter === Filter.ACTIVE_ALL) return matchesSearch;
    if (filter === Filter.INVITED) return false;

    const hasLicense = subscriptionId
      ? !!licenseAssignmentsState[membership.user_id]?.subscriptions.includes(subscriptionId)
      : !!licenseAssignmentsState[membership.user_id]?.subscriptions.length;

    if (filter === Filter.ACTIVE_ASSIGNED) return hasLicense && matchesSearch;
    if (filter === Filter.ACTIVE_UNASSIGNED) return !hasLicense && matchesSearch;
    return false;
  };

/**
 * Applies the filter to invitations (excl. memberships).
 *
 * Invitation filters are rather straightforward:
 * - If the filter is set to "ALL" -> simply return the query results
 * - If the filter is set to "INVITED" -> simply return the query results
 * - Return nothing in any other case, as other filters are for memberships
 *
 * @param filter - chosen membership filter
 * @param searchQuery - text search query to match
 */
export const applyInvitationFilter =
  (filter: Filter, searchQuery: string | null) =>
  (invitation: GetInvitationResponse): boolean => {
    if (filter === Filter.ALL || filter === Filter.INVITED) {
      return matchesSearchQuery(searchQuery, invitation.email, invitation.name);
    }
    return false;
  };

/**
 * Replace a user's full set of license assignments with the result of a
 * `setLicenseAssignmentsForUser` call, which always returns the complete
 * desired list for that user rather than a single created/removed record.
 */
export const setUserAssignments = (
  state: LicenseAssignmentsByUser,
  userId: string,
  assignments: LicenseAssignment[],
): LicenseAssignmentsByUser => {
  const subscriptions = assignments.flatMap((assignment) => assignment.subscription_ids ?? []);
  const next = { ...state };
  if (subscriptions.length === 0) {
    delete next[userId];
  } else {
    next[userId] = { subscriptions };
  }
  return next;
};
